// schedulePresentationModal.jsx
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import DynamicFormInput from "components/form/form";
import React, { use, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLegalResearchRequest, updateLegalResearchRequest } from "services/legalResearchService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_AWAERENESS_REQUEST_STATUS, LEGAL_RESEARCH_REQUEST_STATUS, LEGAL_TRAINING_PROVISION_REQUEST_STATUS } from "utils/constantValues";
import { createResearchSchedule } from "services/researchScheduleService";
import { createLegalAwarenessSchedule, updateLegalAwareness } from "services/legalAwarenessService";
import { createLegalTrainingProvisionSchedule, updateLegalTrainingProvision, updateLegalTrainingProvisionSchedule } from "services/legalTrainingProvision";

interface ScheduleTitlePresentationModalProps {
    data: any;
    setLegalResearch: Function;
    isOpen: boolean;
    onClose: () => void;
    type?: string;
}

const ScheduleTitlePresentationModal: React.FC<ScheduleTitlePresentationModalProps> = ({
    data: singleData,
    setLegalResearch,
    isOpen,
    onClose,
    type,
}) => {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const [employees, setEmployees] = useState([]);
    const contextData = useAuth();
    const [selectedMethod, setSelectedMethod] = useState("");
    const method = [
        { value: "in_person", label: t("InPerson") },
        { value: "online", label: t("Online") },
        { value: "hybrid", label: t("Hybrid") },
    ]

    const proposalSchedule = singleData?.schedules?.find(
        (schedule: any) => schedule.purpose === "title_presentation"
    );

    const [formState, setFormState] = useState(() => {
        return {
            location: "",
            remark: "",
            method: "",
            schedule_datetime: new Date().toISOString().split("T")[0],
        };
    });

    // Update form state when proposalSchedule or singleData changes (for edit functionality)
    React.useEffect(() => {
        if (type === "training" && singleData?.logistics) {
            // For training type, use existing logistics data
            setFormState({
                location: singleData.logistics.location ?? "",
                remark: "",
                method: singleData.logistics.method ?? "",
                schedule_datetime: singleData.logistics.date 
                    ? new Date(singleData.logistics.date).toISOString().split("T")[0]
                    : new Date().toISOString().split("T")[0],
            });
            if (singleData.logistics.method) {
                setSelectedMethod(singleData.logistics.method);
            }
        } else if (proposalSchedule) {
            // For other types with existing schedule, use that data
            setFormState({
                location: proposalSchedule?.location ?? "",
                remark: proposalSchedule?.remark ?? "",
                method: proposalSchedule?.method ?? "",
                schedule_datetime: proposalSchedule?.scheduled_datetime
                    ? new Date(proposalSchedule.scheduled_datetime).toISOString().split("T")[0]
                    : new Date().toISOString().split("T")[0],
            });
        } else {
            // Reset form when there's no existing schedule
            setFormState({
                location: "",
                remark: "",
                method: "",
                schedule_datetime: new Date().toISOString().split("T")[0],
            });
        }
    }, [proposalSchedule, singleData, type]);

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Update formState when selectedMethod changes
    React.useEffect(() => {
        setFormState(prev => ({
            ...prev,
            method: selectedMethod
        }));
    }, [selectedMethod]);

    if (!isOpen) return null;

    const handleAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        const data = new FormData();

        data.append("location", formState.location);
        if (type === "awareness") {
            data.append("remark", formState.remark);
            data.append("scheduled_datetime", formState.schedule_datetime);
            data.append("legal_awarness", singleData.id);
            data.append("purpose", "legal_awareness");
        }
        else if (type === "training") {
            data.append("date", formState.schedule_datetime);
            data.append("method", selectedMethod);
            data.append("training_request", singleData.id);
        }
        else {
            data.append("remark", formState.remark);
            data.append("scheduled_datetime", formState.schedule_datetime);
            data.append("research_request", singleData.id);
            data.append("purpose", "title_presentation");
        }

        try {
            let presentationResponse;
            let response: any;
            if (type === "awareness") {
                presentationResponse = await createLegalAwarenessSchedule(data);
                const awareness_data = new FormData();
                awareness_data.append("current_approval_status", LEGAL_AWAERENESS_REQUEST_STATUS.session_scheduled.value);
                response = await updateLegalAwareness(singleData.id, awareness_data);
            }
            else if (type === "training") {
                if (singleData?.logistics?.id) {
                    presentationResponse = await updateLegalTrainingProvisionSchedule(singleData.logistics.id, data);
                } else {
                    presentationResponse = await createLegalTrainingProvisionSchedule(data);
                    const awareness_data = new FormData();
                    awareness_data.append("status", LEGAL_TRAINING_PROVISION_REQUEST_STATUS.training_scheduled.value);
                    response = await updateLegalTrainingProvision(singleData.id, awareness_data);
                }
            }
            else {
                presentationResponse = await createResearchSchedule(contextData.token, data);
                const research_data = new FormData();
                research_data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value);
                response = await updateLegalResearchRequest(singleData.id, research_data, contextData.token);
            }

            const updatedLogistics = {
                ...singleData.logistics,
                id: presentationResponse?.id || singleData.logistics?.id,
                location: formState.location,
                method: formState.method,
                date: formState.schedule_datetime,
            };

            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
                logistics: updatedLogistics,
                urgency_level: singleData?.urgency_level
                    ? singleData?.urgency_level.charAt(0).toUpperCase() +
                    singleData?.urgency_level.slice(1).toLowerCase()
                    : "High",
            }));

            showToast(
                type === "training" && singleData?.logistics?.id 
                    ? t("TrainingScheduleUpdatedSuccess") 
                    : t("ScheduleForwardedSuccess"), 
                "success"
            );
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateSchedule"), "error");
        } finally {
            setSubmitting(false);
        }

        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <form
                onSubmit={handleAssignSubmit}
                className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 relative"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {type === "training" && singleData?.logistics?.id ? 
                        t("EditTrainingSchedule") : 
                        t("ApprovalAndSettingAppointmentForTitlePresentation")
                    }
                </h2>

                {/* Remark Section */}
                <div className="mb-4">
                    <DynamicFormInput
                        inputs={[
                            {
                                value: formState.location,
                                type: "text",
                                label: t("Location"),
                                name: "location",
                                onChange: handleChange,
                                placeholder: t("LocationPlaceholder"),
                            },
                        ]} />
                </div>
                <label
                    htmlFor="select-department"
                    className="block text-sm font-medium text-gray-700 mb-2"
                >
                    {t("Schedule")}
                </label>
                <input
                    type="date"
                    id="schedule_datetime"
                    name="schedule_datetime"
                    className="w-full p-3 bg-gray-50 border border-gray-300 rounded-md pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                    value={formState?.schedule_datetime}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                    required
                />
                <label
                    htmlFor="select-department"
                    className="block mt-4 text-sm font-medium text-gray-700 mb-2"
                >
                    {type === "training" ? t("TrainingMethod") : t("Remark")}
                </label>
                {type === "training" ?
                    <select
                        className="appearance-none w-full p-3 border border-gray-50 rounded-md pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                        value={selectedMethod}
                        onChange={(e) => {
                            const value = e.target.value;
                            setSelectedMethod(value);
                            setFormState(prev => ({
                                ...prev,
                                method: value
                            }));
                        }}
                    >
                        <option value="">{t("SelectMethod")}</option>
                        {method.map((emp: any) => (
                            <option key={emp.value} value={emp.value}>
                                {emp.label}
                            </option>
                        ))}
                    </select> :
                    <textarea
                        id="remark"
                        name="remark"
                        className="w-full mt-2 p-3 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                        placeholder={t("DetailHere")}
                        value={formState?.remark}
                        onChange={handleChange}
                        required
                    ></textarea>
                }
                {/* Assign Button */}
                <div className="text-center mt-6 flex gap-4">
                    <button
                        type="button"
                        onClick={() => onClose()}
                        className="bg-white hover:bg-gray-200 border border-gray-900 font-bold py-3 px-8 rounded-md w-full"
                    >
                        {t("Back")}
                    </button>
                    <button
                        type="submit"
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-md w-full"
                    >
                        {submitting ? (
                            <div className="flex items-center justify-center">
                                {LoadingIcon} {t("Submitting")}
                            </div>
                        ) : type === "training" && singleData?.logistics?.id ? (
                            t("UpdateSchedule")
                        ) : (
                            t("Schedule")
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ScheduleTitlePresentationModal;